import { useEffect, useState } from "react"
import { PrimaryButton } from "../buttons/PrimaryButton"
import { format } from "date-fns";
import { et } from "date-fns/locale";
import { UserBadge } from "../common/UserBadge";
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { getProductAvailabilityAndPrice, getProductDetails } from "../../api/requests/product";
import { ProductAvailabilityAndDateResponse, ProductListResponse } from "../../api/requests/interfaces/Product";
import { ProfilePublicData } from "../../api/requests/interfaces/UserProfileResponse";
import { getUserPublicProfile } from "../../api/requests/profile";
import { createChatForRental } from "../../api/requests/rental";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../common/Spinner";
import { useAppDispatch } from "../../store/hooks";
import { addNotification, NotificationType } from "../../store/features/notificationsSlice";

export interface ChatRequestViewProps {
    productData: ProductListResponse;
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
}

export const ChatRequestView: React.FC<ChatRequestViewProps> = ({ productData, startDate, endDate, startTime, endTime }) => {
    const [messageText, setMessageText] = useState('Tere, sooviks rentida toodet');
    const [sellerProfile, setSellerProfile] = useState<ProfilePublicData | undefined>();
    const [rentalAvailabilityCost, setRentalAvailabilityCost] = useState<ProductAvailabilityAndDateResponse | undefined>();
    const [loading, setLoading] = useState(false);
    const [loadingRentalRequest, setLoadingRentalRequest] = useState(false);
    
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useEffect(() => {
        getRentalData();
    }, [productData])

    const getRentalData = async () => {
        setLoading(true);
        try {
            const res = await getProductDetails(productData.id);
            const sellerProfile = await getUserPublicProfile(res.userProfileId);
            setSellerProfile(sellerProfile.data);
            const productAvailability = await getProductAvailabilityAndPrice({ startDate: new Date(startDate), endDate: new Date(endDate) }, productData.id);
            setRentalAvailabilityCost(productAvailability)
            setLoading(false);
        } catch (e) {
            console.log("Error: " + e);
        }
    }

    const makeRentalRequest = async () => {
        if (!messageText.length) {
            return;
        }
        setLoadingRentalRequest(true);
        try {
            const res = await createChatForRental({startDate: new Date(startDate), endDate: new Date(endDate), firstMessage: messageText}, productData.id);
            navigate('/rentals-chats?selectedChatId=' + res.chatId);
            setLoadingRentalRequest(false);
        } catch (e) {
            dispatch(addNotification({message: "Renditaotluse esitamisel esines viga", type: NotificationType.ERROR}))
            setLoadingRentalRequest(false);
        }
    }

    return (
        <div className="flex flex-col w-full h-full gap-2">
            {
                (productData && sellerProfile && rentalAvailabilityCost) ?
                        <div className="relative flex flex-col w-full h-[1px] flex-auto py-2 lg:px-4 lg:py-4 lg:shadow-main rounded-xl">
                            <div className="h-full w-full flex justify-center flex-col p-5 lg:p-0">
                                <div className="shadow-main lg:shadow-none rounded-xl border-primary/30 border-[2px] p-4">
                                    <div className="flex items-center gap-2 font-bold text-primary p-2 w-fit rounded-xl"><span className="text-[30px] leading-3">•</span> Uus rendiavaldus</div>
                                    <div className="flex flex-col items-center">
                                        <img className="w-[250px] h-[200px] rounded-xl object-cover mb-1" src={productData.defaultPictureUrl} alt="" />
                                        <div className="text-xl font-semibold">{productData.title}</div>
                                        <div className="flex flex-row items-center py-1 gap-2">
                                            <UserBadge size={"small"} profileIconUrl={sellerProfile.profilePicUrl} profileId={1} />
                                            {sellerProfile.name}
                                        </div>
                                        <h3 className="flex items-center pt-2 font-semibold text-[12px] lg:text-sm"><LocationOnOutlinedIcon /> {productData.location}</h3>
                                        <h3 className="font-bold text-[14px] lg:text-[16px] py-1"> <span className="text-[30px] mr-1">{productData.dailyPrice}</span>
                                            €/päev</h3>
                                        <h4 className="font-bold text-[14px] lg:text-[16px]">Kokku (eeldatav): {rentalAvailabilityCost?.price}€</h4>
                                        <div className="rounded-xl bg-primary text-white px-2 text-[13px] lg:text-sm mt-2">
                                            {format(startDate, 'dd. MMM', { locale: et })} {format(startDate, 'HH:mm')} – {format(endDate, 'dd. MMM', { locale: et })} {format(endDate, 'HH:mm')}
                                        </div>
                                        <div className="flex flex-row items-center">
                                            {rentalAvailabilityCost.isAvailable ?
                                                <div className="w-[8px] h-[8px] bg-green-500 rounded-full mr-2"></div>
                                                :
                                                <div className="w-[8px] h-[8px] bg-red-500 rounded-full mr-2"></div>
                                            }
                                            {rentalAvailabilityCost?.isAvailable ? "saadaval" : "pole saadaval"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex flex-row h-[80px] mt-2">
                                <div className="flex flex-row gap-2 border-solid border-2 border-gray-400 p-2 flex-1 rounded-lg">
                                    <div className="flex-1">
                                        <textarea placeholder="Kirjuta" onChange={(e) => setMessageText(e.target.value)} value={messageText} className="h-[50px] resize-none pb-0 border-none border-black w-full focus:outline-none focus:ring-0 focus:border-none focus:select-none" />
                                    </div>
                                </div>
                                <div className="w-[85px] pl-2 flex justify-center">
                                    {
                                        !loadingRentalRequest ?
                                        <PrimaryButton title="Saada" textSize="m" onClick={() => makeRentalRequest()} />
                                        :
                                        <Spinner size="small"/>
                                    }
                                </div>
                            </div>
                        </div>
                    :
                    loading ?
                        <div className="w-full flex justify-center">
                            <Spinner />
                        </div>
                        :
                        <></>
            }
        </div>
    )
}